import { useState } from "react";
import { DataTable, type Column } from "../components/DataTable";
import { defaultRange } from "../components/DateRangePicker";
import { useTableQuery } from "../api/useTableQuery";
import { QueryError } from "../components/QueryError";
import { money, dateTime } from "../format";

// Shape matches the GET /portal/transactions response verbatim. The money
// fields are Postgres NUMERIC and the handler serializes with
// json.dumps(..., default=str), so they arrive as decimal *strings*.
interface Transaction {
  logical_key: string;
  // The caller's own id (API ingestion); null for SFTP rows, which are
  // identified by their row number in the uploaded file instead.
  client_txn_id: string | null;
  source_row_number: number | null;
  transaction_date_time: string;
  created_at: string;
  transaction_amount: string;
  transaction_amount_cad: string;
  transaction_currency_code: string;
  transaction_source: string;
  // Overall status rolled up server-side from the transaction's reports,
  // worst first. The string is the display label and the f_status value.
  status: TransactionStatus;
  reports: { candidate_id: string; report_type: string; status: string }[];
}

export const TRANSACTION_STATUSES = [
  "Failed", "Pending", "Cancelled", "Withdrawn", "Submitted", "Not reportable",
] as const;
type TransactionStatus = (typeof TRANSACTION_STATUSES)[number];

const STATUS_PILL: Record<TransactionStatus, string> = {
  Failed: "pill bad", Pending: "pill warn", Cancelled: "pill mute",
  Withdrawn: "pill withdrawn", Submitted: "pill ok", "Not reportable": "pill mute",
};

/** The rollup hides which report is responsible, so the pill's tooltip names
 *  each one ("EFTR: ERROR") -- the reason a transaction reads Failed. */
function statusTitle(r: Transaction): string | undefined {
  if (!r.reports?.length) return undefined;
  return r.reports.map((x) => `${x.report_type}: ${x.status}`).join("\n");
}

/** The id a customer recognises: theirs for API transactions, the file row
 *  for SFTP ones. It is also the only thing that tells a resubmission apart
 *  from the rejected original, which share date, amount and source. */
export function transactionId(r: Transaction): string {
  if (r.client_txn_id) return r.client_txn_id;
  if (r.source_row_number != null) return `SFTP row ${r.source_row_number}`;
  return "—";
}

const COLUMNS: Column<Transaction>[] = [
  { header: "Transaction ID", sortKey: "client_txn_id",
    filter: { kind: "text", param: "f_txn_id" },
    render: (r) => transactionId(r) },
  { header: "Status", sortKey: "status",
    filter: { kind: "select", param: "f_status" },
    render: (r) => <span className={STATUS_PILL[r.status] ?? "pill mute"} title={statusTitle(r)}>{r.status}</span> },
  { header: "Transaction date", sortKey: "transaction_date_time",
    render: (r) => dateTime(r.transaction_date_time) },
  { header: "Ingested", sortKey: "created_at", filter: { kind: "dateRange" },
    render: (r) => dateTime(r.created_at) },
  { header: "Amount", sortKey: "transaction_amount", numeric: true,
    filter: { kind: "numberRange", minParam: "f_amount_min", maxParam: "f_amount_max" },
    render: (r) => money(r.transaction_amount) },
  { header: "Currency", sortKey: "transaction_currency_code",
    filter: { kind: "select", param: "f_currency" },
    render: (r) => r.transaction_currency_code },
  { header: "CAD", sortKey: "transaction_amount_cad", numeric: true,
    filter: { kind: "numberRange", minParam: "f_cad_min", maxParam: "f_cad_max" },
    render: (r) => money(r.transaction_amount_cad) },
  { header: "Source", sortKey: "transaction_source",
    filter: { kind: "select", param: "f_source" },
    render: (r) => r.transaction_source },
];

/** clientId is set only when an internal user is inside a customer's
 *  context; customer-role users are scoped by their own JWT. */
export function TransactionsPage({ clientId }: { clientId?: string } = {}) {
  const [{ from, to }, setRange] = useState(defaultRange());
  const q = useTableQuery<Transaction>("/portal/transactions",
    clientId ? { from, to, client_id: clientId } : { from, to });

  return (
    <div>
      {q.isLoading && <p>Loading…</p>}
      {q.error && <QueryError error={q.error} what="transactions" />}
      {!q.error && (
        <DataTable
          live={q.liveBinding}
          columns={COLUMNS}
          rows={q.items}
          total={q.total}
          limit={q.limit}
          offset={q.offset}
          onPageChange={q.setOffset}
          rowKey={(r) => r.logical_key}
          sort={q.sort}
          dir={q.dir}
          onSortChange={q.setSort}
          filters={q.filters}
          onFilterChange={q.setFilter}
          onClearFilters={q.clearFilters}
          facets={{ f_status: [...TRANSACTION_STATUSES], f_currency: q.facets.currency ?? [], f_source: q.facets.source ?? [] }}
          dateRange={{
            from, to,
            // Narrowing the range can shrink `total` below the current
            // offset (e.g. offset=100 on page 3, then a 12-row range),
            // which would otherwise request a page that no longer exists.
            onChange: (f, t) => { setRange({ from: f, to: t }); q.setOffset(0); },
          }}
        />
      )}
    </div>
  );
}
